import { useEffect, useState } from 'react';
import type { PaymentListItem } from '@pet/shared';
import { getPayments, ReceiptApiError } from '../lib/paymentReadClient';
import { formatCentsAsCurrency } from '../lib/money';
import { formatIsoDate } from '../lib/displayDate';
import { useListScroll } from '../lib/listScroll';
import {
  DEFAULT_PAYMENT_FILTERS,
  DEFAULT_SORT,
  SORT_OPTIONS,
  chipOptions,
  filterPayments,
  fromChip,
  isDateSort,
  monthGroups,
  paymentActiveParts,
  paymentChoices,
  sortPayments,
  toChip,
  type PaymentFilters,
  type SortKey,
} from '../lib/readFilters';
import TopBar from '../components/TopBar';
import FilterPanel, { FilterStatus, NoMatch } from '../components/FilterPanel';
import { shortPropertyLabel } from './spending/controls';
import PaymentDetail from './PaymentDetail';
import type { FixDraft } from '../lib/fixDraft';
import styles from './Payments.module.css';

interface PaymentsProps {
  onDone: () => void;
  // "Fix this payment" (AD-19), passed through to the detail.
  onFix?: (draft: FixDraft, resetIngestionId: string | null) => void;
}

type Screen =
  | { name: 'loading' }
  | { name: 'ready'; payments: PaymentListItem[] }
  | { name: 'error'; message: string };

const NOUNS = { one: 'payment', other: 'payments' };

// Local HTTP-status -> message mapping, matching the mapPostError precedent.
// A 401 never logs the user out (AD-06) — it shows a retryable message.
function mapGetError(err: unknown): string {
  if (err instanceof ReceiptApiError && err.status === 401) {
    return "Your session couldn't be confirmed. Please try again.";
  }
  return "We couldn't load your payments. Please try again.";
}

// A response without `properties` (an api older than this web, during a deploy, or an old
// fixture) is read as no houses: the card shows no house label and matches no house chip.
function withProperties(p: PaymentListItem): PaymentListItem {
  return { ...p, properties: p.properties ?? [] };
}

// The house's short label for one house, "Both houses" for more than one, none for none.
function houseLabel(p: PaymentListItem): string | null {
  if (p.properties.length > 1) return 'Both houses';
  if (p.properties.length === 1) return shortPropertyLabel(p.properties[0].address);
  return null;
}

export default function Payments({ onDone, onFix }: PaymentsProps) {
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Filters and sort live here, so they survive a trip to a payment and back (V7);
  // Home unmounts this screen and resets them.
  const [filters, setFilters] = useState<PaymentFilters>(DEFAULT_PAYMENT_FILTERS);
  const [sort, setSort] = useState<SortKey>(DEFAULT_SORT);
  const [panelOpen, setPanelOpen] = useState(false);
  const rememberScroll = useListScroll(selectedId);

  useEffect(() => {
    let cancelled = false;
    // A reload after a delete keeps the list shown until the refetch resolves, so the scroll
    // position comes back (useListScroll restores against the list, not a loading page).
    setScreen((s) => (s.name === 'ready' ? s : { name: 'loading' }));
    getPayments()
      .then((payments) => {
        if (!cancelled) setScreen({ name: 'ready', payments: payments.map(withProperties) });
      })
      .catch((err) => {
        if (!cancelled) setScreen({ name: 'error', message: mapGetError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // The panel and the list each open at their top.
  function openPanel() {
    setPanelOpen(true);
    window.scrollTo(0, 0);
  }
  function closePanel() {
    setPanelOpen(false);
    window.scrollTo(0, 0);
  }
  function clearAll() {
    setFilters(DEFAULT_PAYMENT_FILTERS);
    setSort(DEFAULT_SORT);
  }
  function setFilter(key: keyof PaymentFilters) {
    return (chip: string) => setFilters((f) => ({ ...f, [key]: fromChip(chip) }));
  }

  if (selectedId !== null) {
    return (
      <PaymentDetail
        paymentId={selectedId}
        backLabel="Back to payments"
        onBack={() => setSelectedId(null)}
        onFix={onFix}
        onDeleted={() => {
          setSelectedId(null);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  const payments = screen.name === 'ready' ? screen.payments : [];
  const choices = paymentChoices(payments);
  const shown = sortPayments(filterPayments(payments, filters), sort);

  if (panelOpen && screen.name === 'ready') {
    return (
      <FilterPanel
        sections={[
          { heading: 'Sort', options: SORT_OPTIONS, selected: sort, onSelect: (v) => setSort(v as SortKey) },
          { heading: 'House', options: chipOptions(choices.houses), selected: toChip(filters.house), onSelect: setFilter('house') },
          { heading: 'Who paid', options: chipOptions(choices.whoPaid), selected: toChip(filters.whoPaid), onSelect: setFilter('whoPaid') },
          { heading: 'Year', options: chipOptions(choices.years), selected: toChip(filters.year), onSelect: setFilter('year') },
          { heading: 'Paid to', options: chipOptions(choices.payees), selected: toChip(filters.paidTo), onSelect: setFilter('paidTo') },
        ]}
        shownCount={shown.length}
        nouns={NOUNS}
        onClearAll={clearAll}
        onClose={closePanel}
      />
    );
  }

  // With no rows there is nothing to filter: no action, no panel.
  const topBar = (
    <TopBar
      title="Payments"
      backLabel="Home"
      onBack={onDone}
      action={payments.length > 0 ? { label: 'Filter & sort', onClick: openPanel } : undefined}
    />
  );

  if (screen.name === 'loading') {
    return (
      <main className={styles.container}>
        {topBar}
        <p className={styles.statusText} aria-busy="true">
          Loading payments…
        </p>
      </main>
    );
  }

  if (screen.name === 'error') {
    return (
      <main className={styles.container}>
        {topBar}
        <p className={styles.banner} role="alert">
          {screen.message}
        </p>
        <button className={styles.primaryButton} onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </main>
    );
  }

  const renderCard = (p: PaymentListItem) => {
    const house = houseLabel(p);
    return (
      <button
        key={p.id}
        type="button"
        className={styles.paymentCard}
        onClick={() => {
          rememberScroll();
          setSelectedId(p.id);
        }}
        aria-label={`${p.payee}, ${formatCentsAsCurrency(p.totalCents)}, ${formatIsoDate(p.date)}${house ? `, ${house}` : ''} — view details`}
      >
        <span className={styles.cardTitleRow}>
          <span className={styles.cardPayee}>{p.payee}</span>
          <span className={styles.cardAmount}>{formatCentsAsCurrency(p.totalCents)}</span>
        </span>
        <span className={styles.cardDetail}>{formatIsoDate(p.date)}</span>
        {house && <span className={styles.cardDetail}>{house}</span>}
        <span className={styles.cardDetail}>
          {p.paymentSource === 'OWNER_FUNDED'
            ? `Paid by ${p.ownerName ?? 'an owner'}`
            : 'Paid by the LLC'}
          {p.paymentMethod ? ` · ${p.paymentMethod}` : ''}
        </span>
        <span className={styles.cardDetail}>
          {p.lineCount} {p.lineCount === 1 ? 'expense' : 'expenses'}
        </span>
      </button>
    );
  };

  return (
    <main className={styles.container}>
      {topBar}

      {payments.length === 0 ? (
        <p className={styles.emptyNote}>No payments recorded yet.</p>
      ) : shown.length === 0 ? (
        <NoMatch nouns={NOUNS} onClearAll={clearAll} />
      ) : (
        <>
          {/* A count only: no dollar total summed from this capped page (AD-17, .claude/rules/data-model-aggregation.md). */}
          <FilterStatus
            shown={shown.length}
            total={payments.length}
            nouns={NOUNS}
            activeParts={paymentActiveParts(filters, sort, choices)}
            onClearAll={clearAll}
          />
          {isDateSort(sort) ? (
            monthGroups(shown).map((g) => (
              <section key={g.key} className={styles.monthSection}>
                <h2 className={styles.monthHeading}>{g.label}</h2>
                <div className={styles.cardList}>{g.rows.map(renderCard)}</div>
              </section>
            ))
          ) : (
            <div className={styles.cardList}>{shown.map(renderCard)}</div>
          )}
          <div className={styles.navRow}>
            <button className={styles.secondaryButton} onClick={() => window.scrollTo(0, 0)}>
              Back to top
            </button>
          </div>
        </>
      )}
    </main>
  );
}
